import {
  ActivityType,
  FocusModeTemplateType,
  HabitPackType
} from 'interfaces/common.interface';
import {
  MarketplaceEntityFilterValue,
  MarketplaceOwnerFilterValue,
  PackAndFocusEntityType
} from 'enums';
import {
  MarketplaceEntityFilter,
  MarketplaceOwnerFilter,
  MarketplaceListItem,
  MarketplaceListSources,
  MARKETPLACE_ENTITY_FILTERS,
  MARKETPLACE_OWNER_FILTERS
} from 'interfaces/marketplace.interface';
import { t } from 'services/i18n.service';
import { LOCAL_STORAGE } from 'constants/general';
import { WEBVIEW_ROUTES } from 'constants/routes';
import { getLocalStorage } from 'utils/helpers/storage';
import { isAccessTokenValid } from 'utils/auth-token-validation.util';
import { isCurrentPath } from 'utils/routes.util';

export const collectHabitPackActivities = (
  pack: HabitPackType
): ActivityType[] => [
  ...(pack.morning_activities ?? []),
  ...(pack.break_activities ?? []),
  ...(pack.evening_activities ?? []),
  ...(pack.standalone_activities ?? [])
];

export interface GlobalHabitLibraryGroup {
  packId: string;
  packName: string;
  activities: ActivityType[];
}

/**
 * Flattens approved marketplace packs into per-pack habit groups for the
 * Global Habit Library, filtered by habit name and sorted by pack name.
 */
export const getGlobalHabitLibraryGroups = (
  packs: HabitPackType[],
  searchTerm: string
): GlobalHabitLibraryGroup[] => {
  const term = searchTerm.trim().toLowerCase();
  return packs
    .map((pack) => ({
      packId: pack.id,
      packName: pack.pack_name ?? '',
      activities: collectHabitPackActivities(pack).filter((activity) =>
        term ? activity.name?.toLowerCase().includes(term) : true
      )
    }))
    .filter((group) => group.activities.length > 0)
    .sort((groupA, groupB) => groupA.packName.localeCompare(groupB.packName));
};

export const getHabitPackActivityCount = (pack: HabitPackType): number =>
  collectHabitPackActivities(pack).length;

export const getHabitPackTotalDurationSeconds = (
  pack: HabitPackType
): number => {
  const activitiesDuration = collectHabitPackActivities(pack).reduce(
    (sum, activity) => sum + (activity.duration_seconds ?? 0),
    0
  );
  const routineDuration =
    (pack.morning_routine_duration_seconds ?? 0) +
    (pack.evening_routine_duration_seconds ?? 0);
  return activitiesDuration + routineDuration;
};

export const habitPackHasVideo = (pack: HabitPackType): boolean => {
  if (pack.description_video_url || pack.welcome_video_url) {
    return true;
  }
  return collectHabitPackActivities(pack).some(
    (activity) => (activity.video_urls ?? []).length > 0
  );
};

export const formatDurationLabel = (totalSeconds: number): string => {
  if (totalSeconds <= 0) {
    return '';
  }
  if (totalSeconds >= 60) {
    return t('market.time_minutes', {
      count: Math.max(1, Math.round(totalSeconds / 60))
    });
  }
  return t('market.time_seconds', { count: totalSeconds });
};

export const getFocusTemplateItemCount = (
  template: FocusModeTemplateType
): number =>
  (template.allowed_apps?.length ?? 0) + (template.allowed_urls?.length ?? 0);

export const focusTemplateHasVideo = (
  template: FocusModeTemplateType
): boolean =>
  Boolean(template.description_video_url || template.welcome_video_url);

export const isHabitPackOwner = (
  pack: HabitPackType,
  userId: string | undefined
): boolean => Boolean(userId && pack.user_id === userId);

export const isFocusTemplateOwner = (
  template: FocusModeTemplateType,
  userId: string | undefined
): boolean => Boolean(userId && template.author_id === userId);

export const canManageMarketplaceEntity = (
  isOwner: boolean,
  isUserAdmin: boolean
): boolean => isOwner || isUserAdmin;

export const isFocusOnlyHabitPack = (pack: HabitPackType): boolean =>
  [
    ...(pack.morning_activities ?? []),
    ...(pack.break_activities ?? []),
    ...(pack.evening_activities ?? [])
  ].length === 0;

const getItemSortName = (item: MarketplaceListItem): string => {
  if (item.kind === PackAndFocusEntityType.HABIT_PACK) {
    return (item.habitPack.pack_name ?? '').toLowerCase();
  }
  return (item.focusTemplate.name ?? '').toLowerCase();
};

export const mergeMarketplaceItems = (
  packs: HabitPackType[],
  templates: FocusModeTemplateType[]
): MarketplaceListItem[] => {
  const items: MarketplaceListItem[] = [
    ...packs.map(
      (habitPack): MarketplaceListItem => ({
        kind: PackAndFocusEntityType.HABIT_PACK,
        habitPack
      })
    ),
    ...templates.map(
      (focusTemplate): MarketplaceListItem => ({
        kind: PackAndFocusEntityType.FOCUS_TEMPLATE,
        focusTemplate
      })
    )
  ];

  return items.sort((itemA, itemB) =>
    getItemSortName(itemA).localeCompare(getItemSortName(itemB))
  );
};

export const filterMarketplaceItems = (
  items: MarketplaceListItem[],
  filter: MarketplaceEntityFilter
): MarketplaceListItem[] => {
  if (filter === MarketplaceEntityFilterValue.ALL) {
    return items;
  }
  if (filter === MarketplaceEntityFilterValue.PACKS) {
    return items.filter((item) => item.kind === PackAndFocusEntityType.HABIT_PACK);
  }
  return items.filter(
    (item) => item.kind === PackAndFocusEntityType.FOCUS_TEMPLATE
  );
};

export const parseMarketplaceEntityFilter = (
  value: string | null
): MarketplaceEntityFilter => {
  if (
    value &&
    MARKETPLACE_ENTITY_FILTERS.includes(value as MarketplaceEntityFilter)
  ) {
    return value as MarketplaceEntityFilter;
  }
  return MarketplaceEntityFilterValue.ALL;
};

export const parseMarketplaceOwnerFilter = (
  value: string | null
): MarketplaceOwnerFilter => {
  if (
    value &&
    MARKETPLACE_OWNER_FILTERS.includes(value as MarketplaceOwnerFilter)
  ) {
    return value as MarketplaceOwnerFilter;
  }
  return MarketplaceOwnerFilterValue.EVERYONE;
};

export const isMineOwnerFilter = (
  owner: MarketplaceOwnerFilter
): owner is typeof MarketplaceOwnerFilterValue.MINE =>
  owner === MarketplaceOwnerFilterValue.MINE;

export const hasMarketplaceAuthSession = (): boolean =>
  isAccessTokenValid(getLocalStorage(LOCAL_STORAGE.AUTH_BEARER_TOKEN));

export const isPublicWebviewMarketplace = (): boolean =>
  isCurrentPath(WEBVIEW_ROUTES.WEBVIEW_MARKETPLACE) &&
  !hasMarketplaceAuthSession();

export const canUseMarketplaceAuthActions = (userId?: string): boolean =>
  Boolean(userId) || hasMarketplaceAuthSession();

export const canInstallOrUninstallMarketplaceEntity = (
  userId?: string
): boolean => canUseMarketplaceAuthActions(userId);

export const canManageMineMarketplaceCatalog = (userId?: string): boolean =>
  canUseMarketplaceAuthActions(userId);

export const canCreateMarketplaceEntity = (userId?: string): boolean =>
  canUseMarketplaceAuthActions(userId);

export const getMarketplaceToolbarFilters = (): {
  value: MarketplaceEntityFilter;
  label: string;
}[] => [
  { value: MarketplaceEntityFilterValue.ALL, label: t('market.filter_all') },
  {
    value: MarketplaceEntityFilterValue.PACKS,
    label: t('market.filter_packs')
  },
  {
    value: MarketplaceEntityFilterValue.TEMPLATES,
    label: t('market.filter_templates')
  }
];

export const getMarketplaceOwnerFilters = (): {
  value: MarketplaceOwnerFilter;
  label: string;
}[] => [
  {
    value: MarketplaceOwnerFilterValue.EVERYONE,
    label: t('market.filter_everyone')
  },
  {
    value: MarketplaceOwnerFilterValue.MINE,
    label: t('market.filter_mine')
  }
];

export const getMarketplaceListForFilter = (
  entityFilter: MarketplaceEntityFilter,
  ownerFilter: MarketplaceOwnerFilter,
  sources: MarketplaceListSources
): MarketplaceListItem[] => {
  const merged = isMineOwnerFilter(ownerFilter)
    ? mergeMarketplaceItems(sources.userPacks, sources.userTemplates)
    : mergeMarketplaceItems(sources.publicPacks, sources.publicTemplates);
  return filterMarketplaceItems(merged, entityFilter);
};
